import { useEffect, useRef, useState } from 'react';

interface TurnstileOptions {
  sitekey: string;
  action: string;
  theme: 'light';
  size: 'flexible';
  callback: (token: string) => void;
  'expired-callback': () => void;
  'error-callback': () => void;
}

interface TurnstileApi {
  render(container: HTMLElement, options: TurnstileOptions): string;
  remove(widgetId: string): void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

const scriptId = 'cloudflare-turnstile-script';
let scriptPromise: Promise<TurnstileApi> | null = null;

function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise((resolve, reject) => {
    const existing = document.getElementById(scriptId) as HTMLScriptElement | null;
    const script = existing ?? document.createElement('script');
    const handleLoad = () => window.turnstile
      ? resolve(window.turnstile)
      : reject(new Error('Turnstile did not initialise.'));
    const handleError = () => reject(new Error('Turnstile could not be loaded.'));

    script.addEventListener('load', handleLoad, { once: true });
    script.addEventListener('error', handleError, { once: true });
    if (!existing) {
      script.id = scriptId;
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
  });
  return scriptPromise;
}

interface TurnstileWidgetProps {
  action: string;
  siteKey: string;
  onVerify: (token: string) => void;
  onUnavailable: () => void;
}

export function TurnstileWidget({ action, siteKey, onVerify, onUnavailable }: TurnstileWidgetProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [message, setMessage] = useState('Preparing secure verification…');

  useEffect(() => {
    let active = true;
    let widgetId: string | null = null;
    const container = containerRef.current;
    if (!container) return undefined;

    void loadTurnstile()
      .then((turnstile) => {
        if (!active) return;
        widgetId = turnstile.render(container, {
          sitekey: siteKey,
          action,
          theme: 'light',
          size: 'flexible',
          callback: (token) => {
            setMessage('Secure verification complete.');
            onVerify(token);
          },
          'expired-callback': () => {
            setMessage('Secure verification expired. Please complete it again.');
            onUnavailable();
          },
          'error-callback': () => {
            setMessage('Secure verification could not be completed. Please try again.');
            onUnavailable();
          },
        });
      })
      .catch(() => {
        if (!active) return;
        setMessage('Secure verification is temporarily unavailable.');
        onUnavailable();
      });

    return () => {
      active = false;
      if (widgetId && window.turnstile) window.turnstile.remove(widgetId);
      container.replaceChildren();
    };
  }, [action, onUnavailable, onVerify, siteKey]);

  return (
    <div className="turnstile-control">
      <div ref={containerRef} />
      <small aria-live="polite">{message}</small>
    </div>
  );
}
